import { Link, useParams } from "react-router-dom";

import { refusalText } from "../../../api/errors";
import { useApiQuery } from "../../../api/hooks";
import type { ElementView, Pending } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Breadcrumb, Card, EmptyState, Loading, NeededByLine, Pill, Tabs } from "../../../ui";
import { ElementActions } from "../components/ElementActions";
import { LockBanner } from "../components/LockBanner";
import { ElementMarks, LineList } from "../components/marks";
import { ChecksPanel, ContentFacts, ElementLink, HistoryPanel, NotesPanel } from "../components/panels";
import { chapterPath, elementPath, libraryPath, symbolsPath } from "../lib/paths";
import { whenWords } from "../lib/text";
import { kindWord } from "../lib/words";

/** The screen at /elements/:id: one element with its lines, notes, checks and history. An item opens its own page. */
export function ElementPage() {
  const { id = "" } = useParams();
  const query = useApiQuery("element", { id }, { enabled: !!id });
  if (query.isPending) return <Loading>Reading the element</Loading>;
  if (query.isError) {
    return (
      <Card>
        <EmptyState title="This element could not be opened">{refusalText(query.error)}</EmptyState>
      </Card>
    );
  }
  return <ElementContent view={query.data} />;
}

export function ElementContent({ view }: { view: ElementView }) {
  const me = useCurrentMe();
  const row = view.element;
  const editable = me.active?.roles.includes("engineer") ?? false;
  const quiet = row.state === "withdrawn" || row.state === "removed";

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="element-title">
        <Breadcrumb crumbs={view.breadcrumb} />
        <h1 id="element-title">{row.name}</h1>
        <p className="element-meta">
          <span>{kindWord(row.kind)}</span> <code>{row.code}</code> <ElementMarks element={row} />
          {row.your_own_work && <Pill>Your own work</Pill>}
          {row.origin === "loaded" && row.discipline && <Pill tone="loaded">Loaded, {row.discipline}</Pill>}
          {row.origin === "work" && row.discipline && <Pill>{row.discipline}</Pill>}
        </p>
        {row.kind === "item" && (
          <p>
            <Link to={elementPath(row)}>Open the item page</Link>
          </p>
        )}
        <LockBanner elementId={row.id} lock={view.lock} />
        {row.definition ? <p className="definition">{row.definition}</p> : <p className="muted">No definition yet.</p>}
        <NeededByLine neededBy={row.needed_by} />
        <People view={view} />
        <Decisions row={row} />
        {/* A withdrawn element offers only "Propose again"; a removed one offers nothing (the actions decide). */}
        <ElementActions element={row} editable={editable} usedIn={view.used_in} />
        <p className="row quiet-links">
          <Link to={libraryPath({ q: row.name, element: row.id })}>Find it in the stored libraries</Link>
          {(row.kind === "item" || row.kind === "component_kind") && <Link to={symbolsPath(row.id)}>Pick its symbol</Link>}
          {row.kind === "spec_chapter" && <Link to={chapterPath(row.code)}>Open the chapter editor</Link>}
        </p>
      </Card>

      <Card aria-label="What it holds">
        <h2>What it holds</h2>
        <ContentFacts row={row} />
        {row.kind !== "item" && view.used_in > 0 && (
          <p className="muted">Used in {view.used_in} {view.used_in === 1 ? "place" : "places"}.</p>
        )}
      </Card>

      {row.pending && <PendingCard pending={row.pending} />}

      <Card aria-label="Lines">
        <h2>Lines</h2>
        {view.lines.length === 0 ? <p className="muted">No lines hang on this element yet.</p> : <LineList lines={view.lines} element={row} />}
      </Card>

      <Card>
        <Tabs
          label="More about this element"
          tabs={[
            { id: "notes", label: "Notes", count: view.notes.length, panel: <NotesPanel elementId={row.id} notes={view.notes} canNote={!quiet} /> },
            { id: "checks", label: "Checks", count: view.checks.length, panel: <ChecksPanel checks={view.checks} /> },
            { id: "history", label: "History", count: view.history.length, panel: <HistoryPanel history={view.history} /> },
            {
              id: "files",
              label: "Files",
              count: view.files.length,
              panel:
                view.files.length === 0 ? (
                  <p className="muted">It is not written to any file yet.</p>
                ) : (
                  <ul>
                    {view.files.map((path) => (
                      <li key={path}>
                        <code>{path}</code>
                      </li>
                    ))}
                    <li>
                      <Link to="/files">Open the files view</Link>
                    </li>
                  </ul>
                ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

function People({ view }: { view: ElementView }) {
  const row = view.element;
  const lines: string[] = [];
  if (row.originator) lines.push(`Started by ${row.originator.display}`);
  if (row.added_by) lines.push(`Added by hand by ${row.added_by.display}`);
  if (row.accepted_by) lines.push(`Accepted by ${row.accepted_by.display}`);
  if (row.validated_by) lines.push(`Validated by ${row.validated_by.display}${row.validated_at ? ` on ${whenWords(row.validated_at)}` : ""}`);
  if (lines.length === 0) return null;
  return <p className="muted people">{lines.join(". ")}.</p>;
}

function Decisions({ row }: { row: ElementView["element"] }) {
  return (
    <>
      {row.state === "sent_back" && row.sme_comment && <p className="decision decision--sent-back">The SME sent this back: {row.sme_comment}</p>}
      {row.state === "rejected" && row.sme_reason && <p className="decision decision--rejected">The SME rejected this: {row.sme_reason}</p>}
      {row.removal && (
        <p className="decision">
          Marked for removal: {row.removal.reason}
          {row.removal.status === "sent_back" && row.removal.sme_comment ? `. The SME sent the removal back: ${row.removal.sme_comment}` : ""}
        </p>
      )}
      {row.not_used_by_us && <p className="decision">Not used by us: {row.not_used_by_us.note}</p>}
      {row.same_item_as && <p className="muted">Also called: {row.same_item_as}</p>}
      {row.replaces_id && (
        <p className="muted">
          It replaces <ElementLink id={row.replaces_id} />.
        </p>
      )}
      {row.state === "withdrawn" && row.replaced_by_id && (
        <p className="decision">
          Replaced by <ElementLink id={row.replaced_by_id} />.
        </p>
      )}
    </>
  );
}

function PendingCard({ pending }: { pending: Pending }) {
  return (
    <Card aria-label="Next version">
      <h2>{pending.light ? "Edited since you validated" : "Next version"}</h2>
      <p>
        {pending.status === "sent_back"
          ? `The SME sent this change back${pending.sme_comment ? `: ${pending.sme_comment}` : "."}`
          : pending.light
            ? "A small edit of wording. The SME settles it in one click."
            : "The SME has not decided on this change yet. What the SME validated stays official."}
      </p>
      <dl className="facts">
        <div className="facts__row">
          <dt>Name</dt>
          <dd>{pending.name}</dd>
        </div>
        <div className="facts__row">
          <dt>Definition</dt>
          <dd>{pending.definition}</dd>
        </div>
        {pending.code && (
          <div className="facts__row">
            <dt>New code</dt>
            <dd>{pending.code}</dd>
          </div>
        )}
        <div className="facts__row">
          <dt>Changes the meaning</dt>
          <dd>
            {pending.changes_meaning ? "Yes" : "No"}
            {pending.reason ? `: ${pending.reason}` : ""}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
